import { normalizeEmail } from "../identity/identity";
import type { Company, Contact, Lead, Membership, PartyChannel } from "../../lib/types";
import {
  ALLOWED_MESSAGE_HEADERS,
  MAILBOX_FOLDERS,
  type AllowedMessageHeader,
  type CommunicationMessage,
  type CommunicationThread,
  type MailboxFolder,
  type MessageDirection,
  type ThreadLinkReason,
  type ThreadLinkState,
} from "./contracts";

/**
 * Correlação determinística de mensagens (Plano 02).
 *
 * Todas as funções deste arquivo são puras. Elas nunca criam Lead, nunca
 * escolhem "o Lead mais recente" e devolvem `unlinked` sempre que a decisão
 * pertencer a uma pessoa.
 */

const SUBJECT_PREFIX = /^\s*(?:re|res|res\.|enc|enc\.|fwd|fw|encaminhada|encaminhado)\s*(?:\[\d+\])?\s*:\s*/i;

type RuntimeRecord = Record<string, unknown>;

function asRecord(value: unknown): RuntimeRecord | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as RuntimeRecord)
    : undefined;
}

function nonEmptyText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function safeNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isMailboxFolder(value: unknown): value is MailboxFolder {
  return typeof value === "string" && (MAILBOX_FOLDERS as readonly string[]).includes(value);
}

function threadingValidationError(): Error {
  return new Error("validation_error: os dados de threading de comunicação são inválidos.");
}

/** Remove delimitadores angulares e caixa para comparar `Message-ID` de servidores distintos. */
export function normalizeMessageId(value: string | undefined | null): string | null {
  if (!nonEmptyText(value)) return null;
  const candidate = value.trim().replace(/^</, "").replace(/>$/, "").trim().toLowerCase();
  if (!candidate || /\s/.test(candidate) || !candidate.includes("@")) return null;
  return candidate;
}

export function parseReferences(value: string | undefined | null): string[] {
  if (!nonEmptyText(value)) return [];
  const found = value.match(/<[^>]+>/g) ?? value.trim().split(/\s+/);
  const normalized = found.map((item) => normalizeMessageId(item)).filter((item): item is string => Boolean(item));
  return [...new Set(normalized)];
}

export function normalizeSubject(value: string | undefined | null): string {
  let candidate = (typeof value === "string" ? value : "").trim();
  let guard = 0;
  while (SUBJECT_PREFIX.test(candidate) && guard < 10) {
    candidate = candidate.replace(SUBJECT_PREFIX, "");
    guard += 1;
  }
  return candidate.replace(/\s+/g, " ").trim().toLowerCase();
}

export interface ParsedAddress {
  name?: string;
  address: string;
  normalized: string | null;
}

/** Preserva o valor original exibível e devolve o normalizado usado em comparações. */
export function parseAddress(value: string | undefined | null): ParsedAddress | null {
  const raw = (typeof value === "string" ? value : "").trim();
  if (!raw) return null;
  const angled = raw.match(/^(.*)<([^>]+)>\s*$/);
  const address = (angled ? angled[2] : raw).trim();
  const name = angled ? angled[1].trim().replace(/^"(.*)"$/, "$1").trim() : "";
  return { name: name || undefined, address, normalized: normalizeEmail(address) };
}

export function parseAddressList(value: string | undefined | null): ParsedAddress[] {
  if (!nonEmptyText(value)) return [];
  const entries: string[] = [];
  let current = "";
  let quoted = false;
  let angleDepth = 0;
  let escaped = false;
  for (const character of value) {
    if (escaped) {
      current += character;
      escaped = false;
      continue;
    }
    if (character === "\\" && quoted) {
      current += character;
      escaped = true;
      continue;
    }
    if (character === '"') quoted = !quoted;
    if (!quoted && character === "<") angleDepth += 1;
    if (!quoted && character === ">") angleDepth = Math.max(0, angleDepth - 1);
    if (character === "," && !quoted && angleDepth === 0) {
      entries.push(current);
      current = "";
      continue;
    }
    current += character;
  }
  entries.push(current);
  return entries
    .map((item) => parseAddress(item))
    .filter((item): item is ParsedAddress => Boolean(item));
}

function fnv1a(value: string, seed: number): number {
  let hash = seed;
  for (const character of value) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** Hash estável e determinístico usado como evidência complementar de duplicata. */
export function contentHash(parts: readonly (string | number | undefined)[]): string {
  try {
    if (!Array.isArray(parts)) throw threadingValidationError();
    const normalized: (string | number | undefined)[] = [];
    for (let index = 0; index < parts.length; index += 1) {
      if (!Object.prototype.hasOwnProperty.call(parts, index)) throw threadingValidationError();
      const part = parts[index];
      if (part !== undefined && typeof part !== "string" && (typeof part !== "number" || !Number.isFinite(part)))
        throw threadingValidationError();
      normalized.push(part);
    }
    const serialized = normalized.map((part) => (part === undefined ? "" : part)).join("\u0000");
    return `${fnv1a(serialized, 2166136261).toString(16).padStart(8, "0")}${fnv1a(serialized, 16777619).toString(16).padStart(8, "0")}`;
  } catch {
    throw threadingValidationError();
  }
}

/** Chave de deduplicação IMAP: caixa + pasta + UIDVALIDITY + UID. */
export function mailboxDedupeKey(
  mailboxId: string,
  folder: MailboxFolder,
  uidValidity: number,
  uid: number,
): string {
  if (!nonEmptyText(mailboxId) || !isMailboxFolder(folder) || !safeNonNegativeInteger(uidValidity) || !safeNonNegativeInteger(uid))
    throw threadingValidationError();
  return `${mailboxId}:${folder}:${uidValidity}:${uid}`;
}

export function messageDedupeKey(message: CommunicationMessage): string | null {
  try {
    const record = asRecord(message);
    if (
      !record ||
      !nonEmptyText(record.mailboxId) ||
      !isMailboxFolder(record.folder) ||
      !nonEmptyText(record.externalMessageId) ||
      !nonEmptyText(record.contentHash)
    ) return null;
    if (record.uid === undefined || record.uidValidity === undefined) return null;
    if (!safeNonNegativeInteger(record.uid) || !safeNonNegativeInteger(record.uidValidity)) return null;
    return mailboxDedupeKey(record.mailboxId, record.folder, record.uidValidity, record.uid);
  } catch {
    return null;
  }
}

export interface DuplicateCandidate {
  mailboxId: string;
  folder: MailboxFolder;
  uid?: number;
  uidValidity?: number;
  externalMessageId: string;
  contentHash: string;
}

export type DuplicateEvidence = "uid" | "message_id" | "content_hash";

export interface DuplicateDecision {
  duplicate: boolean;
  existing?: CommunicationMessage;
  evidence?: DuplicateEvidence;
}

interface DedupeMessageFields {
  message: CommunicationMessage;
  mailboxId: string;
  folder: MailboxFolder;
  uid?: number;
  uidValidity?: number;
  externalMessageId: string;
  contentHash: string;
}

function normalizeDuplicateCandidate(value: unknown): DuplicateCandidate | undefined {
  try {
    const record = asRecord(value);
    if (
      !record ||
      !nonEmptyText(record.mailboxId) ||
      !isMailboxFolder(record.folder) ||
      !nonEmptyText(record.externalMessageId) ||
      !nonEmptyText(record.contentHash) ||
      (record.uid !== undefined && !safeNonNegativeInteger(record.uid)) ||
      (record.uidValidity !== undefined && !safeNonNegativeInteger(record.uidValidity))
    ) return undefined;
    const normalized: DuplicateCandidate = {
      mailboxId: record.mailboxId,
      folder: record.folder,
      externalMessageId: record.externalMessageId,
      contentHash: record.contentHash,
    };
    if (record.uid !== undefined) normalized.uid = record.uid;
    if (record.uidValidity !== undefined) normalized.uidValidity = record.uidValidity;
    return normalized;
  } catch {
    return undefined;
  }
}

function normalizeDedupeMessage(value: unknown): DedupeMessageFields | undefined {
  try {
    const record = asRecord(value);
    if (
      !record ||
      !nonEmptyText(record.mailboxId) ||
      !isMailboxFolder(record.folder) ||
      !nonEmptyText(record.externalMessageId) ||
      !nonEmptyText(record.contentHash) ||
      (record.uid !== undefined && !safeNonNegativeInteger(record.uid)) ||
      (record.uidValidity !== undefined && !safeNonNegativeInteger(record.uidValidity))
    ) return undefined;
    const normalized: DedupeMessageFields = {
      message: value as CommunicationMessage,
      mailboxId: record.mailboxId,
      folder: record.folder,
      externalMessageId: record.externalMessageId,
      contentHash: record.contentHash,
    };
    if (record.uid !== undefined) normalized.uid = record.uid;
    if (record.uidValidity !== undefined) normalized.uidValidity = record.uidValidity;
    return normalized;
  } catch {
    return undefined;
  }
}

/**
 * Duplicata é decidida primeiro pelo UID e só depois por Message-ID/hash, que
 * são evidências complementares — inclusive quando o servidor reindexa.
 */
export function findDuplicate(
  messages: readonly CommunicationMessage[],
  candidate: DuplicateCandidate,
): DuplicateDecision {
  const normalizedCandidate = normalizeDuplicateCandidate(candidate);
  if (!normalizedCandidate || !Array.isArray(messages)) throw threadingValidationError();
  const normalizedMessages: DedupeMessageFields[] = [];
  for (let index = 0; index < messages.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(messages, index)) throw threadingValidationError();
    const normalized = normalizeDedupeMessage(messages[index]);
    if (!normalized) throw threadingValidationError();
    normalizedMessages.push(normalized);
  }
  const key =
    normalizedCandidate.uid !== undefined && normalizedCandidate.uidValidity !== undefined
      ? mailboxDedupeKey(
          normalizedCandidate.mailboxId,
          normalizedCandidate.folder,
          normalizedCandidate.uidValidity,
          normalizedCandidate.uid,
        )
      : null;
  if (key) {
    const byUid = normalizedMessages.find(
      (message) =>
        message.uid !== undefined &&
        message.uidValidity !== undefined &&
        mailboxDedupeKey(message.mailboxId, message.folder, message.uidValidity, message.uid) === key,
    );
    if (byUid) return { duplicate: true, existing: byUid.message, evidence: "uid" };
  }
  const byMessageId = normalizedMessages.find(
    (message) =>
      message.mailboxId === normalizedCandidate.mailboxId &&
      message.folder === normalizedCandidate.folder &&
      message.externalMessageId === normalizedCandidate.externalMessageId,
  );
  if (byMessageId) return { duplicate: true, existing: byMessageId.message, evidence: "message_id" };
  const byHash = normalizedMessages.find(
    (message) =>
      message.mailboxId === normalizedCandidate.mailboxId &&
      message.folder === normalizedCandidate.folder &&
      message.contentHash === normalizedCandidate.contentHash,
  );
  if (byHash) return { duplicate: true, existing: byHash.message, evidence: "content_hash" };
  return { duplicate: false };
}

export type UidValidityDecision = "stable" | "initialize" | "reconcile";

/**
 * Mudança de UIDVALIDITY invalida os UIDs anteriores, mas não autoriza apagar
 * nem reimportar a caixa inteira: o histórico permanece e a reconciliação é
 * limitada às mensagens conhecidas.
 */
export function uidValidityDecision(
  knownUidValidity: number | undefined,
  observedUidValidity: number,
): UidValidityDecision {
  if (!safeNonNegativeInteger(observedUidValidity)) return "reconcile";
  if (knownUidValidity !== undefined && !safeNonNegativeInteger(knownUidValidity)) return "reconcile";
  if (knownUidValidity === undefined) return "initialize";
  return knownUidValidity === observedUidValidity ? "stable" : "reconcile";
}

export function allowedHeaders(
  headers: Record<string, string>,
): Partial<Record<AllowedMessageHeader, string>> {
  try {
    const record = asRecord(headers);
    if (!record) return {};
    const allowlist = new Set<string>(ALLOWED_MESSAGE_HEADERS);
    const result: Partial<Record<AllowedMessageHeader, string>> = {};
    for (const [name, value] of Object.entries(record)) {
      if (typeof value !== "string") continue;
      const key = name.trim().toLowerCase();
      if (allowlist.has(key)) result[key as AllowedMessageHeader] = value;
    }
    return result;
  } catch {
    return {};
  }
}

export interface ThreadResolutionInput {
  ownerId: string;
  mailboxId: string;
  direction: MessageDirection;
  externalMessageId: string;
  inReplyTo?: string;
  references: readonly string[];
  counterpartAddress: string;
  subject: string;
  /** Preenchido apenas quando a mensagem nasce de um comando do CosmosLeads. */
  originLeadId?: string;
}

export interface ThreadResolutionIndex {
  threads: readonly CommunicationThread[];
  messages: readonly CommunicationMessage[];
  partyChannels: readonly PartyChannel[];
  contacts: readonly Contact[];
  companies: readonly Company[];
  leads: readonly Lead[];
  memberships: readonly Membership[];
}

export interface ThreadResolution {
  linkState: ThreadLinkState;
  linkReason: ThreadLinkReason;
  threadId?: string;
  companyId?: string;
  contactId?: string;
  leadId?: string;
  funnelId?: string;
  normalizedCounterpart: string | null;
}

function denseArray(value: unknown): value is unknown[] {
  if (!Array.isArray(value)) return false;
  for (let index = 0; index < value.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(value, index)) return false;
  }
  return true;
}

function normalizeThreadResolutionInput(value: unknown): ThreadResolutionInput | undefined {
  try {
    const record = asRecord(value);
    const references = record?.references;
    const inReplyTo = record?.inReplyTo;
    const originLeadId = record?.originLeadId;
    if (
      !record ||
      !nonEmptyText(record.ownerId) ||
      !nonEmptyText(record.mailboxId) ||
      (record.direction !== "inbound" && record.direction !== "outbound") ||
      !nonEmptyText(record.externalMessageId) ||
      typeof record.counterpartAddress !== "string" ||
      typeof record.subject !== "string" ||
      !denseArray(references) ||
      !references.every((reference) => typeof reference === "string") ||
      (inReplyTo !== undefined && typeof inReplyTo !== "string") ||
      (originLeadId !== undefined && !nonEmptyText(originLeadId))
    ) return undefined;
    const normalized: ThreadResolutionInput = {
      ownerId: record.ownerId,
      mailboxId: record.mailboxId,
      direction: record.direction,
      externalMessageId: record.externalMessageId,
      references: [...references],
      counterpartAddress: record.counterpartAddress,
      subject: record.subject,
    };
    if (inReplyTo !== undefined) normalized.inReplyTo = inReplyTo;
    if (originLeadId !== undefined) normalized.originLeadId = originLeadId;
    return normalized;
  } catch {
    return undefined;
  }
}

function normalizeThreadResolutionIndex(value: unknown): ThreadResolutionIndex | undefined {
  try {
    const record = asRecord(value);
    const threads = record?.threads;
    const messages = record?.messages;
    const partyChannels = record?.partyChannels;
    const contacts = record?.contacts;
    const companies = record?.companies;
    const leads = record?.leads;
    const memberships = record?.memberships;
    if (
      !record ||
      !denseArray(threads) ||
      !denseArray(messages) ||
      !denseArray(partyChannels) ||
      !denseArray(contacts) ||
      !denseArray(companies) ||
      !denseArray(leads) ||
      !denseArray(memberships)
    ) return undefined;
    return {
      threads: [...threads] as CommunicationThread[],
      messages: [...messages] as CommunicationMessage[],
      partyChannels: [...partyChannels] as PartyChannel[],
      contacts: [...contacts] as Contact[],
      companies: [...companies] as Company[],
      leads: [...leads] as Lead[],
      memberships: [...memberships] as Membership[],
    };
  } catch {
    return undefined;
  }
}

function funnelOfLead(index: ThreadResolutionIndex, leadId: string): string | undefined {
  return index.memberships.find((membership) => membership.leadId === leadId)?.funnelId;
}

function unlinked(reason: ThreadLinkReason, normalizedCounterpart: string | null, extra: Partial<ThreadResolution> = {}): ThreadResolution {
  return { linkState: "unlinked", linkReason: reason, normalizedCounterpart, ...extra };
}

/**
 * Ordem obrigatória do Plano 02: References/In-Reply-To, conversa já vinculada
 * ao mesmo endpoint, endpoint exato da Company/Contact e, por fim, fila humana.
 */
export function resolveThreadLink(
  input: ThreadResolutionInput,
  index: ThreadResolutionIndex,
): ThreadResolution {
  let normalizedCounterpart: string | null = null;
  try {
    const normalizedInput = normalizeThreadResolutionInput(input);
    const normalizedIndex = normalizeThreadResolutionIndex(index);
    if (!normalizedInput || !normalizedIndex) return unlinked("no_match", normalizedCounterpart);
    input = normalizedInput;
    index = normalizedIndex;
    normalizedCounterpart = normalizeEmail(input.counterpartAddress);

  if (input.originLeadId) {
    const lead = index.leads.find((item) => item.id === input.originLeadId && !item.archived);
    if (lead) {
      return {
        linkState: "linked",
        linkReason: "outbound_origin",
        leadId: lead.id,
        companyId: lead.companyId,
        contactId: lead.primaryContactId,
        funnelId: funnelOfLead(index, lead.id),
        normalizedCounterpart,
      };
    }
  }

  // 1. References/In-Reply-To apontando para um Message-ID conhecido.
  const chain = [...(input.inReplyTo ? [input.inReplyTo] : []), ...input.references];
  for (const reference of chain) {
    const known = index.messages.find(
      (message) =>
        message.ownerId === input.ownerId &&
        message.mailboxId === input.mailboxId &&
        message.externalMessageId === reference,
    );
    if (!known) continue;
    const thread = index.threads.find(
      (item) =>
        item.id === known.threadId &&
        item.ownerId === input.ownerId &&
        item.mailboxId === input.mailboxId,
    );
    if (!thread) continue;
    return {
      linkState: thread.linkState === "linked" ? "linked" : thread.linkState,
      linkReason: "references",
      threadId: thread.id,
      companyId: thread.companyId,
      contactId: thread.contactId,
      leadId: thread.leadId,
      funnelId: thread.funnelId,
      normalizedCounterpart,
    };
  }

  if (!normalizedCounterpart) return unlinked("no_match", normalizedCounterpart);

  // 2. Conversa já vinculada ao mesmo endpoint dentro da mesma caixa.
  const sameEndpoint = index.threads.filter(
    (thread) =>
      thread.ownerId === input.ownerId &&
      thread.mailboxId === input.mailboxId &&
      thread.counterpartAddress === normalizedCounterpart &&
      thread.linkState === "linked" &&
      thread.leadId,
  );
  if (sameEndpoint.length) {
    const leadIds = [...new Set(sameEndpoint.map((thread) => thread.leadId as string))];
    if (leadIds.length > 1) return unlinked("ambiguous_lead", normalizedCounterpart);
    const reference = sameEndpoint[0];
    const normalizedSubject = normalizeSubject(input.subject);
    const sameSubject = sameEndpoint.find((thread) => thread.normalizedSubject === normalizedSubject);
    return {
      linkState: "linked",
      linkReason: "known_thread",
      threadId: sameSubject?.id,
      companyId: reference.companyId,
      contactId: reference.contactId,
      leadId: reference.leadId,
      funnelId: reference.funnelId,
      normalizedCounterpart,
    };
  }

  // 3. Endpoint exato pertencente a uma Company e, quando pessoal, ao Contact certo.
  const matchingChannels = index.partyChannels.filter(
    (channel) =>
      channel.type === "email" &&
      channel.status === "active" &&
      channel.normalizedValue === normalizedCounterpart,
  );
  if (!matchingChannels.length) return unlinked("no_match", normalizedCounterpart);

  const companyIds = [...new Set(matchingChannels.map((channel) => channel.companyId))];
  if (companyIds.length > 1) return unlinked("ambiguous_company", normalizedCounterpart);

  const companyId = companyIds[0];
  const company = index.companies.find((item) => item.id === companyId);
  if (!company || company.status === "merged") return unlinked("no_match", normalizedCounterpart, { companyId });

  const contactIds = [
    ...new Set(matchingChannels.map((channel) => channel.contactId).filter((id): id is string => Boolean(id))),
  ];
  if (contactIds.length > 1) return unlinked("conflicting_contact", normalizedCounterpart, { companyId });
  const contactId = contactIds[0];
  if (contactId && !index.contacts.some((contact) => contact.id === contactId && contact.companyId === companyId))
    return unlinked("conflicting_contact", normalizedCounterpart, { companyId });

  const companyLeads = index.leads.filter(
    (lead) => lead.companyId === companyId && !lead.archived && !lead.mergedIntoId,
  );
  if (!companyLeads.length) return unlinked("no_match", normalizedCounterpart, { companyId, contactId });
  if (companyLeads.length > 1) return unlinked("ambiguous_lead", normalizedCounterpart, { companyId, contactId });

  const lead = companyLeads[0];
  return {
    linkState: "linked",
    linkReason: "exact_endpoint",
    companyId,
    contactId,
    leadId: lead.id,
    funnelId: funnelOfLead(index, lead.id),
    normalizedCounterpart,
  };
  } catch {
    return unlinked("no_match", normalizedCounterpart);
  }
}

/** Detecta autorresposta para não tratá-la como resposta humana. */
export function isAutoResponder(headers: Partial<Record<AllowedMessageHeader, string>>): boolean {
  try {
    const record = asRecord(headers);
    if (!record) return true;
    const autoSubmitted = record["auto-submitted"];
    const xAutoreply = record["x-autoreply"];
    if (autoSubmitted !== undefined && typeof autoSubmitted !== "string") return true;
    if (xAutoreply !== undefined && typeof xAutoreply !== "string") return true;
    const normalizedAutoSubmitted = typeof autoSubmitted === "string" ? autoSubmitted.trim().toLowerCase() : undefined;
    if (normalizedAutoSubmitted && normalizedAutoSubmitted !== "no") return true;
    return Boolean(xAutoreply);
  } catch {
    return true;
  }
}
